<template>
    <div class="comment-container" ref="el" :class="{'--replying': isReplying, '--reply': comment.parent_id}">
        <div class="header-container">
            <div class="avatar-container" :class="{'online': isOnline}">
                <img class="avatar" :src="avatar" alt="" v-tippy="{ placement: 'top', boundary: 'window' }"
                     :content="name"
                />
            </div>
            <span class="name" v-html="name"></span>
            <span class="date-created">{{ createdAt }}</span>
        </div>

        <vue-editor class="content" :editorOptions="editorSettings" ref="comment"
                    v-model="content"
        />

        <div class="parent-comment-container" v-if="comment.parentMessage" >
            <div class="header-container">
                <div class="avatar-container" :class="{'online': isOnline}">
                    <img class="avatar" :src="parentAvatar" alt="" v-tippy="{ placement: 'top', boundary: 'window' }"
                         :content="parentName"
                    />
                </div>
                <span class="name">{{ parentName }}</span>
            </div>

            <vue-editor class="content parent-content" :editorOptions="editorSettings" ref="parentComment"
                        v-model="parentComment"
            />

            <div class="mentions-container">
                <component @click.native="onThumbnailClick($event, mention.id, mention.fileType, true)" :is="thumbnailComponent"
                           v-for="mention in parentAssetMentions" :key="mention.id"
                           :item="assetById(mention.id, mention.fileType)"
                           :inComments="true"
                />
            </div>
        </div>

        <div class="mentions-container">
            <component @click.native="onThumbnailClick($event, mention.id, mention.fileType)" :is="thumbnailComponent"
                       v-for="mention in assetMentions" :key="mention.id"
                       :item="assetById(mention.id, mention.fileType)"
                       :inComments="true"
            />
        </div>

        <div class="tools-container" v-if="!comment.deleted_at">
            <span class="reply-btn" @click="replyToComment">Reply</span>
            <span v-if="isAuthorOfComment" class="material-icons" @click="deleteComment">delete</span>
        </div>
    </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import Thumbnail from '@/components/app/projects/Thumbnail';
import AvatarMixins from "@/mixins/Avatar";
import FilenameMixin from "@/mixins/Filename";
import { mapGetters, mapState } from "vuex";

export default {
    name: "CommentTemplate",
    mixins: [AvatarMixins, FilenameMixin],

    props: {
        comment: Object,
    },

    components: {
        VueEditor,
    },

    data() {
        return {
            editorSettings: {
                readOnly: true,
                formats: ["bold", "italic", "mention"],
                modules: {
                    toolbar: false,
                }
            },

            createdAt: '',
            calculateCreatedAtInterval: null,
            content: '',
            delta: null,
            assetMentions: [],
            parentComment: '',
            parentDelta: null,
            parentAssetMentions: [],

            commentDeletedMessage: '{"ops":[{"attributes":{"italic":true},"insert":"This message has been deleted."},{"insert":"\\n"}]}',
        }
    },

    async mounted() {
        this.calculateCreatedAt();
        this.calculateCreatedAtInterval = setInterval(() => {
           this.calculateCreatedAt();
        }, 15000)

        if (this.comment.deleted_at) {
            this.delta = JSON.parse(this.commentDeletedMessage);
        } else {
            this.delta = JSON.parse(this.comment.text);
            let mentionsList = this.assetMentionsFromDelta(this.delta);

            for await (const mention of mentionsList) {
                if (!this.assetById(mention.id, mention.fileType)) {
                    await this.$store.dispatch("Projects/refreshProjectTree");
                    break;
                }
            }

            this.assetMentions = mentionsList;
        }

        // getting rid of mentioned user when it is a replied comment
        let foundFirstUserMention = false;
        if (this.comment.parentMessage) {
            this.delta.ops.forEach((item, key) => {
                if (item?.insert?.mention &&
                    item.insert.mention.denotationChar == "@" &&
                    !foundFirstUserMention) {
                    delete this.delta.ops[key];
                    foundFirstUserMention = true;
                }
            })
        }

        this.$refs.comment.quill.setContents(this.delta);

        if (this.comment.parentMessage) {
            if (this.comment.parentMessage.deleted_at) {
                this.parentDelta = JSON.parse(this.commentDeletedMessage);
            } else {
                this.parentDelta = JSON.parse(this.comment.parentMessage.text);
                let mentionsList = this.assetMentionsFromDelta(this.parentDelta);

                for await (const mention of mentionsList) {
                    if (!this.assetById(mention.id, mention.fileType)) {
                        await this.$store.dispatch("Projects/refreshProjectTree");
                        break;
                    }
                }

                this.parentAssetMentions = mentionsList;
            }
            this.$refs.parentComment.quill.setContents(this.parentDelta);
        }

        this.$el.querySelectorAll('.mention:not([data-denotation-char="@"])').forEach(el => {
            el.addEventListener('click',  async e => {
                let file_id = el.getAttribute('data-id');
                let file_type = el.getAttribute('data-file-type');

                this.onThumbnailClick(e, file_id, file_type);
            })
        })

        setTimeout(() => {
            ['mouseenter', 'mouseleave'].forEach(event => {
                this.$el.querySelectorAll('.mentions-container img').forEach(el => {
                    el.addEventListener(event, () => {
                        let file_id = el.getAttribute('data-id');
                        this.$el.querySelectorAll(`.mention[data-id="${file_id}"] > span`).forEach(mentionEl => {
                            mentionEl.classList.toggle('--hover');
                        })
                    })
                })

                this.$el.querySelectorAll(`.mention`).forEach(el => {
                    el.addEventListener(event, () => {
                        let file_id = el.getAttribute('data-id');
                        this.$el.querySelectorAll(`.mentions-container img[data-id="${file_id}"]`).forEach(mentionEl => {
                            mentionEl.classList.toggle('--hover');
                        })
                    })
                })
            });
        }, 1000)
    },

    beforeDestroy() {
        clearInterval(this.calculateCreatedAtInterval);
    },

    computed: {
        ...mapState('Comments', ['replyTo', 'currentChannel']),
        ...mapState('Projects', ['bin', 'fileThumbnailObjects']),
        ...mapState('User', ['account']),
        ...mapGetters('Projects', ['assetById', 'userOfActiveProject']),
        ...mapGetters('Comments', ['assetMentionsFromDelta']),
        name() {
            let name = this.getUser.name;
            if (this.comment.account?.suspended_at) name += '<span class="color-warning"> (suspended)</span>';
            if (!this.comment.account && !this.$store.getters['Organizations/memberAccountByUserId'](this.comment.user.id)) name += '<span class="color-warning"> (deleted)</span>';
            return name;
        },
        avatar() {
            return this.getGravatar(this.getUser.email);
        },
        parentName() {
            return this.comment.parentMessage.account.user.name;
        },
        parentAvatar() {
            return this.getGravatar(this.comment.parentMessage.account.user.email);
        },
        getUser() {
            if (!this.comment.account) return this.comment.user;
            else if (this.comment.account.user) return this.comment.account.user;
            else return this.userOfActiveProject(this.comment.account.user_id);
            // else return this.$store.getters['Organizations/user'](this.accountOfAuthor);
        },
        isAuthorOfComment() {
            if (!this.comment.account) return false;
            else return this.comment.account.id == this.account.id;
        },
        isReplying() {
            return this.comment.id == this.replyTo?.id;
        },
        thumbnailComponent() {
            return Thumbnail;
        },
        isOnline() {
            return this.$store.getters['Organizations/isUserOnline'](this.getUser.id);
        },
    },

    watch: {
        'comment.text': function(newVal, oldVal) {
            if (this.comment.deleted_at) {
                this.delta = JSON.parse(this.commentDeletedMessage);
                this.$refs.comment.quill.setContents(this.delta);
                this.assetMentions = [];
            }
        },
        'comment.parentMessage.text': function(newVal, oldVal) {
            if (this.comment.parentMessage.deleted_at) {
                this.parentDelta = JSON.parse(this.commentDeletedMessage);
                this.$refs.parentComment.quill.setContents(this.parentDelta);
                this.parentAssetMentions = [];
            }
        },
    },

    methods: {
        async deleteComment() {
            await this.$store.dispatch('Comments/delete', this.comment.id);
        },

        replyToComment() {
            this.$store.dispatch('Comments/replyTo', this.comment);
            let account_id = this.comment.account.id;
            let mention = this.$store.getters['Comments/findAtValue'](account_id);
            this.$root.commentsEditor.$parent.insertMention('@', mention);
            this.$root.commentsEditor.quill.focus();
        },

        async onThumbnailClick(e, file_id, file_type, isParentComment = false) {
            let file = this.assetById(file_id, file_type, this.currentChannel);

            if (!file) {
                this.$snackbar('Asset is not found.', 'error');
                return;
            }

            if (this.$store.getters["Projects/fileHasTag"](file, 'comment_upload', 1)) {
                this.$root.ViewCommentUploadModal.username = isParentComment ? this.parentName : this.name;
                this.$root.ViewCommentUploadModal.avatar = isParentComment ? this.parentAvatar : this.avatar;
                this.$root.ViewCommentUploadModal.filename = this.stripTimestamp(file.name);
                this.$root.ViewCommentUploadModal.created_at = this.createdAt;
                this.$root.ViewCommentUploadModal.image = file;
                this.$root.ViewCommentUploadModal.imagesList = isParentComment ?
                    this.parentAssetMentions.map(mention => this.assetById(mention.id, mention.fileType)) :
                    this.assetMentions.map(mention => this.assetById(mention.id, mention.fileType));

                UI.showModal(UI.modals.viewCommentUpload.el);
            } else {
                this.$root.projectTree.onFileSelection(e, this.bin.id, file);
            }
        },

        calculateCreatedAt() {
            let createdAt = Moment(this.comment.created_at).fromNow();
            if (createdAt == 'just now ago') this.createdAt = createdAt.split(' ago')[0];
            else this.createdAt = createdAt;
        }
    }
}
</script>

<style lang="scss" scoped>

.comment-container {
    background: #202227;
    width: 100%;
    padding: 10px 12px;
    border-radius: 15px;

    &:not(:last-of-type) {
        margin-bottom: 12px;
    }

    &.--reply {
        //margin-left: auto;
    }

    &.--replying {
        background: var(--item-active-highlight);
    }
}

.header-container {
    display: flex;
    align-items: center;

    .avatar-container {
        position: relative;
        margin-right: 10px;

        &.online:after {
            content: '';
            display: block;
            position: absolute;
            bottom: -2px;
            right: -2px;
            width: 11px;
            height: 11px;
            background: green;
            border-radius: 25px;
            border: 2px solid #202227;
        }
    }

    .name {
        font-weight: bold;
        margin-right: 10px;
    }

    .date-created {
        margin-left: auto;
    }
}

.parent-comment-container {
    border-left: 2px solid var(--primary);
    padding: 10px;
    margin: 10px 0;
    background: rgb(24, 22, 30);
    border-radius: 0 15px 15px 0;

    .avatar {
        width: 25px;
        height: 25px;
    }
}

.content {
    margin: 15px 0;

    ::v-deep .ql-editor {
        padding: 0 !important;

        * {
            user-select: text;
        }

        .mention {
            color: var(--primary);

            &:not([data-denotation-char="@"]) {
                cursor: pointer;
            }

            > span.--hover {
                //font-weight: bold;
                background: var(--primary);
                color: black;
                border-radius: 3px;
            }

            span {
                transition: none;
            }

            .ql-mention-denotation-char {
                display: none;
            }
        }
    }
}

.parent-content {
    margin-botton: 0;
}

.mentions-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 8px;
    grid-row-gap: 8px;

    img {
        aspect-ratio: 1;
        width: 100%;
        object-fit: cover;
        object-position: center;
        cursor: pointer;
        transition: none;
        border: 1px solid transparent;

        &.--hover {
            border: 1px solid var(--primary);
        }
    }

    ::v-deep .placeholder-preview {
        height: 33px;
    }
}

.tools-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;

    span {
        cursor: pointer;

        &.material-icons {
            font-size: 18px;
        }
    }
}

</style>